import React from "react";
import { Plus } from "lucide-react";
import { JobsTab } from "@/components/admin/JobsTab";
import { EventsTab } from "@/components/admin/EventsTab";
import { ResumesPanel } from "@/components/admin/ResumesPanel";
import { SubmissionsTable } from "@/components/admin/SubmissionsTable";
import { PassportRequestsTab } from "@/components/admin/PassportRequestsTab";
import { BriefReviewsTab } from "@/components/admin/BriefReviewsTab";
import { useAdminContext } from "./AdminShell";

const PageHeader = ({ overline, title, action }) => (
    <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
            <p className="overline">{overline}</p>
            <h1 data-testid="dash-page-title" className="mt-1 font-heading font-medium text-2xl sm:text-3xl tracking-tight text-foreground">{title}</h1>
        </div>
        {action}
    </div>
);

export const BriefReviewsPage = () => (
    <>
        <PageHeader overline="Command Center" title="Brief Reviews" />
        <BriefReviewsTab />
    </>
);

export const JobsPage = () => {
    const { jobs, loading, openJobEditor, fetchAll } = useAdminContext();
    return (
        <>
            <PageHeader
                overline="Content"
                title={`Jobs (${jobs.length})`}
                action={
                    <button onClick={() => openJobEditor(null)} data-testid="admin-new-job" className="btn-green inline-flex items-center gap-2">
                        <Plus size={14} /> New Job
                    </button>
                }
            />
            <JobsTab jobs={jobs} loading={loading} onEdit={openJobEditor} onChanged={fetchAll} />
        </>
    );
};

export const EventsPage = () => {
    const { events, loading, openEventEditor, fetchAll } = useAdminContext();
    return (
        <>
            <PageHeader
                overline="Content"
                title={`Events (${events.length})`}
                action={
                    <button onClick={() => openEventEditor(null)} data-testid="admin-new-event" className="btn-navy inline-flex items-center gap-2">
                        <Plus size={14} /> New Event
                    </button>
                }
            />
            <EventsTab events={events} loading={loading} onEdit={openEventEditor} onChanged={fetchAll} />
        </>
    );
};

export const PassportRequestsPage = () => {
    const { passportRequests, fetchAll } = useAdminContext();
    return (
        <>
            <PageHeader overline="Requests" title={`Passport Requests (${passportRequests.length})`} />
            <PassportRequestsTab rows={passportRequests} onChanged={fetchAll} />
        </>
    );
};

export const ApplicationsPage = () => {
    const { submissions } = useAdminContext();
    return (
        <>
            <PageHeader overline="Requests" title={`Applications (${submissions.applications.length})`} />
            <SubmissionsTable
                rows={submissions.applications}
                columns={[
                    { key: "created_at", label: "Date" },
                    { key: "job_title", label: "Job" },
                    { key: "company", label: "Company" },
                    { key: "name", label: "Name" },
                    { key: "email", label: "Email" },
                    { key: "phone", label: "Phone" },
                ]}
            />
        </>
    );
};

export const ResumesPage = () => {
    const { submissions } = useAdminContext();
    return (
        <>
            <PageHeader overline="Requests" title={`Resumes (${submissions.resumes.length})`} />
            <ResumesPanel rows={submissions.resumes} />
        </>
    );
};

export const TalentRequestsPage = () => {
    const { submissions } = useAdminContext();
    return (
        <>
            <PageHeader overline="Requests" title={`Talent Requests (${submissions.talent_requests.length})`} />
            <SubmissionsTable
                rows={submissions.talent_requests}
                columns={[
                    { key: "created_at", label: "Date" },
                    { key: "company_name", label: "Company" },
                    { key: "contact_name", label: "Contact" },
                    { key: "email", label: "Email" },
                    { key: "role_needed", label: "Role" },
                    { key: "employment_type", label: "Type" },
                ]}
            />
        </>
    );
};

export const ContactPage = () => {
    const { submissions } = useAdminContext();
    return (
        <>
            <PageHeader overline="Requests" title={`Contact (${submissions.contacts.length})`} />
            <SubmissionsTable
                rows={submissions.contacts}
                columns={[
                    { key: "created_at", label: "Date" },
                    { key: "name", label: "Name" },
                    { key: "email", label: "Email" },
                    { key: "subject", label: "Subject" },
                    { key: "message", label: "Message" },
                ]}
            />
        </>
    );
};
